/* eslint-disable indent */
import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useLogin from "./hooks/useLogin";
import { LoginStatus } from "./constants/loginStatus";
import {
  getMaintenanceInfo,
  getTenantConfig,
} from "./services/configurationService";
import { setTenantSettings, setSupportedLanguages, clearState } from "./redux/reducers/sessionReducer";
import BaseRoutes from "./routes";
import { baseContainer } from "./index";
import Redirect from "./routes/redirectComponent";
import CookiesHandler from "./components/cookiesHandler";
import TermsPoliciesHandler from "./components/termsPoliciesHandler";
import { useSelector } from "react-redux";
import usePageChange from "./hooks/usePageChange";
import { setPageError } from "./redux/reducers/sessionReducer";
import { useAuth0 } from "@auth0/auth0-react";
import Logger from "./logger";

const BaseApp = ({ routes, screens, documentTitle, appRedirect }) => {
  Logger.of("App.BaseApp").info("BaseApp rendering");
  const dispatch = useDispatch();
  const { loginStatus } = useLogin();
  const [underMaintenance, setUnderMaintenance] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [supportedLanguagesArray, setSupportedLanguagesArray] = useState([]); // the fetched supported languages from maintenance API
  const [loadHomePage, setLoadHomePage] = useState(false);
  const navigate = useNavigate();
  const forceTenant = useMemo(() => baseContainer.getConfig().forceTenant, []);
  const networkError = useSelector((state) => state.session.networkError);

  const { isAuthenticated, isLoading } = useAuth0();
  Logger.of("App.BaseApp.useAuth0").info({ isAuthenticated, isLoading });
  usePageChange(() => {
    // When the URL path changes, reset the page error
    baseContainer.store.dispatch(setPageError(false));
  }, []);

  useEffect(() => {
    if (underMaintenance === null) {
      getMaintenanceInfo()
        .then((res) => {
          setUnderMaintenance(res.data.status);
          setActiveVersion(res.data.activeVersion);
          setSupportedLanguagesArray(res.data.supportedLanguages || []);
        })
        .catch(() => setUnderMaintenance(true));
    }
  }, []);

  useEffect(() => {
    if (loginStatus === LoginStatus.Authorized) {
      if (forceTenant) {
        getTenantConfig()
          .then((res) => {
            dispatch(setTenantSettings(res.data));
            dispatch(setSupportedLanguages(supportedLanguagesArray));
            setLoadHomePage(true);
          })
          .catch(() => setUnderMaintenance(true));
      } else {
        dispatch(setSupportedLanguages(supportedLanguagesArray));
        setLoadHomePage(true);
      }
      //if user navigates to tenant selection page then we need to clear the redux state
    } else if (loginStatus === LoginStatus.TenantSelection) {
      setLoadHomePage(false);
      dispatch(clearState());
    }
  }, [loginStatus, supportedLanguagesArray]);

  const {
    loading: LoadingScreen,
    maintenance: MaintenanceScreen,
    fatalError: FatalErrorScreen,
    login: LoginScreen,
    unAuthorized: UnAuthorizedScreen,
    tenantSelect: TenantSelectScreen,
    routesWrapper: RoutesWrapperScreen,
  } = screens;

  if (networkError) {
    return <FatalErrorScreen />;
  }

  if (
    loginStatus === LoginStatus.LoggingOut ||
    loginStatus === LoginStatus.Pending ||
    underMaintenance === null
  ) {
    return <LoadingScreen />;
  }

  if (underMaintenance) {
    return <MaintenanceScreen />;
  }

  const selectTenantHandler = (tenant) => {
    const selectTenant = async () => {
      await baseContainer.authenticationService.selectTenant(tenant);
      navigate("/");
    };
    selectTenant();
  };

  if (!isAuthenticated && !isLoading) {
    return (
      <>
        <LoginScreen
          onClick={() => baseContainer.authenticationService.login()}
        />
        {screens.cookies && (
          <CookiesHandler cookies={screens.cookies}></CookiesHandler>
        )}
      </>
    );
  }

  return (
    <>
      {loginStatus === LoginStatus.TenantSelection && (
        <TenantSelectScreen onSelect={selectTenantHandler} />
      )}
      {loginStatus === LoginStatus.Authorized && loadHomePage && (
        <>
          {appRedirect &&
            baseContainer.authenticationService.hasRedirectPermissions(
              appRedirect.permissionsKeys
            ) ? (
            <Redirect redirectUrl={appRedirect.url} />
          ) : (
            <>
              {routes && (
                <>
                  {!RoutesWrapperScreen && (
                    <BaseRoutes routes={routes} documentTitle={documentTitle} />
                  )}
                  {RoutesWrapperScreen && (
                    <RoutesWrapperScreen activeVersion={activeVersion}>
                      <BaseRoutes
                        routes={routes}
                        documentTitle={documentTitle}
                      />
                    </RoutesWrapperScreen>
                  )}
                </>
              )}
              {screens.termsPolicies && (
                <TermsPoliciesHandler
                  termsPolicies={screens.termsPolicies}
                ></TermsPoliciesHandler>
              )}
            </>
          )}
        </>
      )}
      {loginStatus === LoginStatus.UnAuthorized && <UnAuthorizedScreen />}
      {screens.cookies && (
        <CookiesHandler cookies={screens.cookies}></CookiesHandler>
      )}
    </>
  );
};

BaseApp.propTypes = {
  documentTitle: PropTypes.string,
  screens: PropTypes.shape({
    loading: PropTypes.any.isRequired,
    maintenance: PropTypes.any.isRequired,
    unAuthorized: PropTypes.any.isRequired,
    login: PropTypes.any.isRequired,
  }).isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      component: PropTypes.element,
      permissions: PropTypes.array,
      titleSuffix: PropTypes.string,
      section: PropTypes.string,
      dynamicTitle: PropTypes.bool,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          path: PropTypes.string.isRequired,
          component: PropTypes.element,
          permissions: PropTypes.array,
          titleSuffix: PropTypes.string,
          section: PropTypes.string,
          dynamicTitle: PropTypes.bool,
        })
      ),
    })
  ),
};

export default BaseApp;
